import { Injectable } from '@visiba/instantiation';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import weekDay from 'dayjs/plugin/weekday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import duration from 'dayjs/plugin/duration';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { I18nAndL10n } from '@application/i18n/mod';

dayjs.extend(isBetween);
dayjs.extend(weekDay);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(duration);
dayjs.extend(customParseFormat);

export enum DateDisplayFormats {
	DEFAULT,
	FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY,
	FULL_DISPLAY_NO_CURRENT_YEAR_AND_WEEKDAY,
	FULL_DISPLAY_NO_CURRENT_YEAR_WEEKDAY_TIME,
	FULL_DISPLAY_NO_TIME_WEEKDAY,
	FULL_DISPLAY_NO_TIME,
	JUST_TIME,
}

@Injectable()
export class DateService {
	/**
	 * Returns a new instance of DayJs
	 */
	public static newDate(date?: Date | string | DateTime, format?: string): VisibaDate {
		if (typeof date === 'string') {
			if (date === '') {
				return dayjs();
			}

			date = DateService.parseStringToUtc(date);
		}

		return dayjs(date, date ? format : undefined);
	}

	/**
	 * Check if the date is valid
	 */
	public isValidDate(date?: Date | string | DateTime): boolean {
		if (date == null) return false;

		return dayjs(date).isValid();
	}

	public static getLocaleSpecificDateFormatTemplate(format = DateDisplayFormats.DEFAULT): string {
		let template = dayjs().localeData().longDateFormat('LLLL');

		switch (format) {
			case DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY: {
				template = template
					.split(' ')
					.filter((part) => !part.match(/[yY]{4}/g))
					.join(' ')
					.replace('dddd,', 'dddd')
					.replace('dddd', '');
				break;
			}
			case DateDisplayFormats.FULL_DISPLAY_NO_TIME_WEEKDAY: {
				template = template
					.split(' ')
					.filter((part) => !part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
					.join(' ')
					.replace('dddd,', 'dddd')
					.replace('dddd', '')
					.replace('YYYY,', 'YYYY');
				break;
			}
			case DateDisplayFormats.FULL_DISPLAY_NO_CURRENT_YEAR_WEEKDAY_TIME: {
				template = template
					.split(' ')
					.filter((part) => !part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
					.join(' ')
					.replace('dddd,', 'dddd')
					.replace('dddd', '')
					.replace('YYYY,', 'YYYY')
					.replace('YYYY', '');
				break;
			}
			case DateDisplayFormats.FULL_DISPLAY_NO_TIME: {
				template = template
					.split(' ')
					.filter((part) => !part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
					.join(' ')
					.replace('YYYY,', 'YYYY');
				break;
			}
			case DateDisplayFormats.JUST_TIME: {
				template = template
					.split(' ')
					.filter((part) => part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
					.join(' ');

				break;
			}
			case DateDisplayFormats.FULL_DISPLAY_NO_CURRENT_YEAR_AND_WEEKDAY: {
				template = template.replace('YYYY,', 'YYYY').replace('YYYY', '').replace('dddd,', 'dddd').replace('dddd', '');

				break;
			}
		}

		return template;
	}

	/**
	 * @param time — If unit is not present, time treated as number of milliseconds
	 */
	public static duration = dayjs.duration;

	/**
	 *
	 */
	public static locale = dayjs.locale;

	/**
	 * The reason why we need to parse the date string to UTC (sometimes) is
	 * because we can receive different types of date strings from the backend.
	 */
	private static parseStringToUtc(dateStr: string): string {
		const char = dateStr[dateStr.length - 1];

		if (char.toLowerCase() === 'z') {
			return dateStr;
		}

		return dateStr + 'Z';
	}

	constructor() {
		// Empty
	}

	/**
	 * Returns a new instance of DayJs
	 *
	 * @deprecated use the "newDate" method instead.
	 */
	public create(date?: Date | string): VisibaDate {
		return this.newDate(date);
	}

	/**
	 * Returns a new instance of VisibaDate (DayJS)
	 */
	public newDate(date?: Date | string, format?: string): VisibaDate {
		return DateService.newDate(date, date ? format : undefined);
	}

	/**
	 * Returns an format template based on loaded locale.
	 */
	public getLocaleSpecificDateFormatTemplate(format = DateDisplayFormats.DEFAULT): string {
		return DateService.getLocaleSpecificDateFormatTemplate(format);
	}

	/**
	 * Will compare "day" values between the argument and current time, resulting
	 * in if the date was indeed _yesterday_.
	 */
	public isYesterday(date: dayjs.Dayjs): boolean {
		return date.isSame(this.create().subtract(1, 'day'), 'day');
	}

	/**
	 * Will compare "day" values between the argument and current time, resulting
	 * in if the date was indeed _today_.
	 */
	public isToday(date: dayjs.Dayjs): boolean {
		return date.isSame(this.create(), 'day');
	}

	/**
	 * Will compare "day" values between the argument and current time, resulting
	 * in if the date was indeed _tomorrow_.
	 */
	public isTomorrow(date: dayjs.Dayjs): boolean {
		return date.isSame(this.create().add(1, 'day'), 'day');
	}

	public isThisWeek(date: dayjs.Dayjs): boolean {
		return date.isSame(this.create(), 'week');
	}

	public isThisYear(date: dayjs.Dayjs): boolean {
		return date.isSame(this.create(), 'year');
	}

	/**
	 * Loads a supported locale by fetching the file
	 */
	public async loadLocale(locale: I18nAndL10n.AvailableVisibaLocales): AsyncResult<void> {
		try {
			if (locale === 'sv') {
				await import('dayjs/locale/sv');
			} else if (locale === 'nb') {
				await import('dayjs/locale/nb');
			} else if (locale === 'fi') {
				await import('dayjs/locale/fi');
			} else if (locale === 'da') {
				await import('dayjs/locale/da');
			} else if (locale === 'en-gb') {
				await import('dayjs/locale/en-gb');
			} else if (locale === 'ar') {
				await import('dayjs/locale/ar');
			} else if (locale === 'fa') {
				await import('dayjs/locale/fa');
			} else if (locale === 'hr') {
				await import('dayjs/locale/hr');
			} else if (locale === 'pt-br') {
				await import('dayjs/locale/pt-br');
			} else if (locale === 'nl') {
				await import('dayjs/locale/nl');
			}

			dayjs.locale(locale);

			return Ok();
		} catch (error) {
			console.error(`Unexpected locale error loading, the provided locale "${locale}" is not supported in dayjs`);

			// To not cause the application to fail to start, we'll just let the user through even
			// though this one failed
			return Ok();
		}
	}

	/**
	 * Since our backend uses a different ISO standard, we need to map theirs to Dayjs's locales
	 */
	public convertExternalLocaleToLocal(external: string): I18nAndL10n.AvailableVisibaLocales {
		const externalLowercase = external.toLocaleLowerCase();

		// Needs to convert some Visiba locales due to not matching directly to Dayjs
		if (externalLowercase === 'en') return 'en-gb';
		if (externalLowercase === 'no') return 'nb';

		const availableLocales: I18nAndL10n.AvailableVisibaLocales[] = ['sv', 'nb', 'fi', 'da', 'en-gb', 'ar', 'fa', 'hr', 'pt-br'];

		if (availableLocales.includes(externalLowercase as I18nAndL10n.AvailableVisibaLocales)) {
			return externalLowercase as I18nAndL10n.AvailableVisibaLocales;
		}

		// Returns en-gb if external did not match anything
		return 'en-gb';
	}
}
